import { ArrowUpRight, Check, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LocalizedLink } from "../../lib/i18nRouting";
import { RENTALS } from "../../data/experiences";
import { formatPrice, shouldShowCurrencyNote } from "../../i18n/pricing";
import Reveal from "../Reveal";

export default function Rentals() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <section className="bg-bone-deep py-24 lg:py-40" data-testid="rentals-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-16 lg:mb-20">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-wider-3 text-moss mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-moss" />
              {t("rentals.eyebrow")}
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-forest leading-[1.04]">
              {t("rentals.title_a")} <span className="italic">{t("rentals.title_b")}</span>.
            </h2>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("rentals.subtitle_a")} <span className="text-forest">{t("rentals.subtitle_b")}</span>.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120} className="max-w-3xl mb-16 lg:mb-20">
          <p className="font-display italic text-2xl md:text-3xl text-forest leading-snug">
            „{t("rentals.pull_quote")}&rdquo;
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {RENTALS.map((p, i) => {
            const price = formatPrice(p.priceRON, lang, p.priceKey);
            return (
              <Reveal key={p.key} delay={i * 120}>
                <div
                  data-testid={`rental-card-${p.key.replace("_","-")}`}
                  className="bg-bone border border-bone-deep p-8 lg:p-10 h-full flex flex-col group hover:bg-forest hover:text-bone transition-colors duration-700"
                >
                  <div className="flex items-start justify-between">
                    <div className="text-[10px] uppercase tracking-wider-3 text-terracotta">
                      {t(`rentals.plans.${p.key}.tagline`)}
                    </div>
                    <span className="text-xs uppercase tracking-wider-3 text-moss group-hover:text-bone/60">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl mt-6 text-forest group-hover:text-bone leading-tight">
                    {t(`rentals.plans.${p.key}.label`)}
                  </h3>
                  <div className="mt-8 pt-6 border-t border-bone-deep group-hover:border-bone/15">
                    <div className="font-display text-5xl md:text-6xl text-forest group-hover:text-bone">
                      {price}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider-3 text-moss group-hover:text-bone/60 mt-2">
                      {t(`rentals.plans.${p.key}.unit`)}
                    </div>
                  </div>
                  <p className="mt-6 text-sm text-muted-foreground group-hover:text-bone/80 leading-relaxed">
                    {t(`rentals.plans.${p.key}.text`)}
                  </p>
                  <LocalizedLink
                    to="/rezervare"
                    data-testid={`rental-cta-${p.key.replace("_","-")}`}
                    className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-wider-3 text-forest group-hover:text-bone link-underline self-start"
                  >
                    {t("rentals.cta")} <ArrowUpRight className="w-4 h-4" />
                  </LocalizedLink>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-16 lg:mt-20">
          <div className="text-xs uppercase tracking-wider-3 text-terracotta mb-6 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-terracotta" />
            {t("rentals.included_title")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-bone-deep border border-bone-deep">
            {[1,2,3,4].map((n) => (
              <div key={n} className="bg-bone p-6 lg:p-7 flex items-start gap-4">
                <Check className="w-5 h-5 text-terracotta stroke-[1.5] shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider-3 text-moss mb-2">0{n}</div>
                  <div className="text-sm text-forest leading-snug">{t(`rentals.included.${n}`)}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={150} className="mt-12 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 border-t border-bone-deep pt-8">
          <Truck className="w-5 h-5 text-forest stroke-[1.5] shrink-0" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            <span className="text-forest">{t("rentals.delivery")}</span> — {t("rentals.delivery_note")}
          </p>
        </Reveal>

        {shouldShowCurrencyNote(lang) && (
          <div className="mt-6 text-[11px] uppercase tracking-wider-3 text-moss" data-testid="currency-note">
            {t("currency_note_short")}
          </div>
        )}
      </div>
    </section>
  );
}
